import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Confetti from "react-confetti";

import cyntraLogo from "../assets/images/cyntra_logo.svg";
import { submit_feedback } from "../utils/feedback_helper";
import { login } from "../utils/authenticate";
import { web_login } from "../store/actions/action";

// Emoji rating scale (1–5) shown as tappable faces.
const FACES = [
  { value: 1, emoji: "😞", label: "AWFUL" },
  { value: 2, emoji: "🙁", label: "POOR" },
  { value: 3, emoji: "😐", label: "OKAY" },
  { value: 4, emoji: "🙂", label: "GOOD" },
  { value: 5, emoji: "😍", label: "GREAT" },
];

// Positive path (4–5): "What stood out?"
const POSITIVE_TAGS = ["Food Quality", "Service", "Speed", "Cleanliness", "Value for Money"];
// Improvement path (1–3): "What went wrong?"
const NEGATIVE_TAGS = [
  "Food Quality", "Order Accuracy", "Waiting Time",
  "Staff Behaviour", "Cleanliness", "Pricing", "Other",
];

// Per-rating copy for the detail step.
const COPY = {
  1: { head: "We're sorry to hear that.", sub: "What went wrong? Pick all that apply.", positive: false },
  2: { head: "We'd love to do better.", sub: "What went wrong? Pick all that apply.", positive: false },
  3: { head: "Thanks for the honest feedback.", sub: "What could we improve? Pick all that apply.", positive: false },
  4: { head: "Glad you had a good time.", sub: "What stood out?", positive: true },
  5: { head: "Glad you enjoyed it.", sub: "What stood out?", positive: true },
};

const MAROON = "#7B1E24";

const Feedback = () => {
  // Route: /f/:store_id  (feedback is scoped to the store)
  const { store_id } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "Store QR";
  const storeName = searchParams.get("store_name") || "";
  const city = searchParams.get("city") || "";

  const dispatch = useDispatch();
  const [step, setStep] = useState("rate"); // "rate" | "detail"
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Store branding (logo + theme colour) fetched via the login/web-login call,
  // which also yields the auth token used for store API calls (stored in redux).
  const [store, setStore] = useState(null);
  const [config, setConfig] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await login({ store_id });
        if (active && resp && resp.isAuthented) {
          // Persist the session (token + store details) so store APIs can be called.
          dispatch(
            web_login({
              token: resp.user?.token,
              store_id: resp.user?.store_id,
              location_id: resp.user?.location_id,
              store_details: resp.store || null,
              store_config: resp.storeConfig || null,
              table_details: resp.table_details || null,
            })
          );
          setStore(resp.store || null);
          setConfig(resp.storeConfig || null);
        }
      } catch (e) {
        console.log("feedback login error", e);
      } finally {
        if (active) setBooting(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [store_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Branding derived from the fetched store (falls back to query params / Cyntra).
  const brandName = store?.store_name || storeName;
  const brandCity = store?.store_city || city;
  const logoUrl = store?.store_display_logo || store?.store_logo || cyntraLogo;
  const accent = config?.general_config?.primary_color || MAROON;

  const meta = rating ? COPY[rating] : null;
  const isPositive = meta ? meta.positive : false;
  const chips = isPositive ? POSITIVE_TAGS : NEGATIVE_TAGS;
  const face = FACES.find((f) => f.value === rating);

  const win = useMemo(() => ({ w: window.innerWidth, h: window.innerHeight }), []);

  const pickFace = (value) => {
    setRating(value);
    setTags([]);
    setError("");
    setStep("detail");
  };

  const toggleTag = (tag) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const goBack = () => {
    setStep("rate");
    setError("");
  };

  const handleSubmit = async () => {
    if (mobile && !/^\d{10}$/.test(mobile)) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }
    setError("");
    setSubmitting(true);

    const payload = {
      store_id,
      rating,
      source,
      sentiment: isPositive ? "positive" : "negative",
      feedback_tags: tags,
      // Primary complaint category (for negative feedback) drives analytics.
      issue_category: isPositive ? null : tags[0] || null,
      comment: comment.trim() || null,
      customer_name: name.trim() || null,
      customer_mobile: mobile.trim() || null,
    };

    const res = await submit_feedback(payload);
    setSubmitting(false);

    if (res && res.status) setDone(true);
    else setError((res && res.message) || "Could not submit. Please try again.");
  };

  // NOTE: these are plain JSX values / render helpers — NOT nested components.
  // Defining components inside the render would give them a new identity every
  // keystroke, remounting the subtree (input loses focus, scroll jumps to top).
  const brandNode = (
    <div style={styles.brand}>
      <img
        src={logoUrl}
        alt={brandName || "Feedback"}
        style={styles.logo}
        onError={(e) => { e.currentTarget.src = cyntraLogo; }}
      />
      {brandName ? <div style={styles.brandName}>{brandName}</div> : null}
      {brandCity ? <div style={styles.brandCity}>{brandCity}</div> : null}
    </div>
  );

  const footerNode = (
    <div style={styles.footer}>
      <span style={styles.footerText}>Powered by</span>
      <img src={cyntraLogo} alt="Cyntra" style={styles.footerLogo} />
    </div>
  );

  // Page shell: one scroll area holding the content + footer. The footer uses
  // margin-top:auto, so it sits at the viewport bottom when content is short and
  // flows right after the content (scroll to reach it) when the page is tall.
  const renderScreen = (content, confetti) => (
    <div style={styles.page}>
      {confetti || null}
      <div style={styles.scrollArea}>
        <div style={styles.wrap}>{content}</div>
        {footerNode}
      </div>
    </div>
  );

  /* ------------------------------- Booting ------------------------------- */
  if (booting) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.scrollArea, justifyContent: "center" }}>
          <div style={{ color: "#8A8378" }}>Loading…</div>
        </div>
      </div>
    );
  }

  /* ------------------------------- Success ------------------------------- */
  if (done) {
    return renderScreen(
      <>
        {brandNode}
        <div style={styles.bigEmoji}>{isPositive ? "🙏" : "💬"}</div>
        <h1 style={styles.head}>Thank you!</h1>
        <p style={styles.sub}>
          {isPositive
            ? "We're thrilled you enjoyed your visit. See you again soon!"
            : "We appreciate your feedback and will work on making it better."}
        </p>
      </>,
      isPositive && <Confetti width={win.w} height={win.h} numberOfPieces={220} recycle={false} />
    );
  }

  /* ---------------------------- Step 1: rate ---------------------------- */
  if (step === "rate") {
    return renderScreen(
      <>
        {brandNode}
        <h1 style={styles.head}>How was your experience today?</h1>
        <p style={styles.sub}>Tap the face that fits best</p>

        <div style={styles.faceRow}>
          {FACES.map((f) => (
            <button key={f.value} type="button" style={styles.faceCol} onClick={() => pickFace(f.value)}>
              <span style={styles.faceCard}>
                <span style={styles.faceEmoji}>{f.emoji}</span>
              </span>
              <span style={styles.faceLabel}>{f.label}</span>
            </button>
          ))}
        </div>
      </>
    );
  }

  /* --------------------------- Step 2: detail --------------------------- */
  return renderScreen(
    <>
        {brandNode}

        <div style={styles.bigEmoji}>{face ? face.emoji : ""}</div>
        <h1 style={styles.head}>{meta.head}</h1>
        <p style={styles.sub}>{meta.sub}</p>

        <div style={styles.chipWrap}>
          {chips.map((tag) => {
            const selected = tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                style={{
                  ...styles.chip,
                  ...(selected ? { background: accent, borderColor: accent, color: "#fff", fontWeight: 600 } : {}),
                }}
              >
                {tag}
              </button>
            );
          })}
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel}>
            Anything else? <span style={styles.optional}>(optional)</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={isPositive ? "Tell us what made it great..." : "Tell us more so we can fix it..."}
            rows={4}
            style={styles.textarea}
          />
        </div>

        {/* Contact box — always shown (optional). Copy adapts to the rating path. */}
        <div style={styles.contactBox}>
          <div style={styles.contactTitle}>
            {isPositive ? "Stay in touch?" : "Want us to make it right?"}
          </div>
          <div style={styles.contactSub}>
            {isPositive
              ? "Leave your details for offers and updates. Totally optional."
              : "Leave your details and the manager will reach out. Totally optional."}
          </div>
          <div style={styles.contactRow}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              style={styles.contactInput}
            />
            <input
              type="tel"
              inputMode="numeric"
              value={mobile}
              maxLength={10}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              placeholder="Phone number"
              style={styles.contactInput}
            />
          </div>
        </div>

        {error ? <div style={styles.error}>{error}</div> : null}

        <div style={styles.actionRow}>
          <button type="button" onClick={goBack} style={styles.backBtn} disabled={submitting}>
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{ ...styles.sendBtn, background: accent, opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? "Sending…" : "Send feedback"}
          </button>
        </div>
    </>
  );
};

const serif = "'Playfair Display', 'Georgia', 'Cambria', 'Times New Roman', serif";
const sans = "'Segoe UI', system-ui, -apple-system, sans-serif";

const styles = {
  // Sticky-footer layout: page fills the viewport, content scrolls, footer is a
  // fixed sibling always at the bottom.
  page: {
    height: "100dvh",
    background: "#F4EFE7",
    display: "flex",
    flexDirection: "column",
    fontFamily: sans,
    color: "#2E2A26",
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 22px 16px",
    boxSizing: "border-box",
  },
  wrap: {
    width: "100%",
    maxWidth: 560,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  footer: {
    flexShrink: 0,
    marginTop: "auto",   // bottom when short; collapses to 0 (after content) when tall
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingTop: 28,
    paddingBottom: 4,
    opacity: 0.75,
  },
  footerText: { fontSize: 13, color: "#8A8378", fontWeight: 500, marginTop: 3 },
  footerLogo: { height: 20, objectFit: "contain" },
  brand: { display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40 },
  logo: { height: 104, width: 104, objectFit: "contain", borderRadius: "50%" },
  brandName: { fontFamily: serif, fontSize: 36, fontWeight: 600, marginTop: 16, letterSpacing: 0.2, color: "#2E2A26" },
  brandCity: { fontFamily: serif, fontSize: 22, color: "#2E2A26", fontWeight: 500, marginTop: 6 },
  head: { fontFamily: serif, fontSize: 26, fontWeight: 600, margin: "6px 0 10px", lineHeight: 1.25, letterSpacing: -0.2 },
  sub: { fontSize: 15, color: "#8A8378", margin: "0 0 30px" },
  bigEmoji: { fontSize: 56, lineHeight: 1, margin: "6px 0 6px" },

  faceRow: { display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" },
  faceCol: {
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  faceCard: {
    width: 76,
    height: 76,
    borderRadius: 18,
    background: "#fff",
    border: "1px solid #EBE4D5",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform .12s ease, box-shadow .12s ease",
  },
  faceEmoji: { fontSize: 38, lineHeight: 1 },
  faceLabel: { fontSize: 13, letterSpacing: 1, color: "#8A8378", fontWeight: 600 },

  chipWrap: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 30 },
  chip: {
    border: "1px solid #E1D9C7",
    background: "#fff",
    color: "#2E2A26",
    borderRadius: 999,
    padding: "10px 20px",
    fontSize: 15,
    cursor: "pointer",
    transition: "all .15s ease",
  },
  chipSelected: { background: MAROON, borderColor: MAROON, color: "#fff", fontWeight: 600 },

  field: { width: "100%", textAlign: "left", marginBottom: 20 },
  fieldLabel: { display: "block", fontSize: 15, fontWeight: 700, marginBottom: 10 },
  optional: { fontWeight: 400, color: "#8A8378" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #E1D9C7",
    borderRadius: 12,
    padding: "14px 16px",
    fontSize: 15,
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit",
    background: "#FDFBF6",
  },

  contactBox: {
    width: "100%",
    boxSizing: "border-box",
    background: "#F1E9D8",
    border: "1px solid #E6DCC6",
    borderRadius: 14,
    padding: "18px",
    textAlign: "left",
    marginBottom: 20,
  },
  contactTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  contactSub: { fontSize: 13.5, color: "#8A8378", marginBottom: 14 },
  contactRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  contactInput: {
    flex: "1 1 160px",
    minWidth: 0,
    boxSizing: "border-box",
    border: "1px solid #E1D9C7",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 15,
    outline: "none",
    fontFamily: "inherit",
    background: "#FDFBF6",
  },

  error: {
    width: "100%",
    boxSizing: "border-box",
    color: "#b91c1c",
    background: "#fdecec",
    border: "1px solid #f3c4c4",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    marginBottom: 16,
    textAlign: "left",
  },

  actionRow: { display: "flex", gap: 14, width: "100%" },
  backBtn: {
    flex: "0 0 auto",
    minWidth: 130,
    background: "#fff",
    color: "#2E2A26",
    border: "1px solid #E1D9C7",
    borderRadius: 12,
    padding: "15px 22px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
  },
  sendBtn: {
    flex: 1,
    background: MAROON,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "15px 22px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default Feedback;
